import {
  CTCSS_TONES,
  DTCS_CODES,
  type RadioProgress,
  type WebChannel,
} from './baofeng-web.ts'
import type { RadioTransport } from './web-serial.ts'

const ACK = 0x06
const BLOCK_SIZE = 64
const CHARSET =
  "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ !`o$%&'()*+,-./|;/=>?@[u]^_"

type FTx800Layout = 'ft8800' | 'ft8900'

export type FTx800Profile = {
  protocol: 'yaesu-ftx800'
  id: string
  vendor: 'Yaesu'
  model: string
  summary: string
  tags: readonly string[]
  baudRate: 9600
  channelMinimum: 1
  channelMaximum: number
  nameLength: 6
  memorySize: number
  layout: FTx800Layout
  checksumOffset: number
  powerLevels: readonly string[]
}

export const FTX800_PROFILES: readonly FTx800Profile[] = [
  {
    protocol: 'yaesu-ftx800',
    id: 'yaesu-ft-8800',
    vendor: 'Yaesu',
    model: 'FT-8800',
    summary: 'Yaesu FT-8800 dual-band mobile with left and right channel banks.',
    tags: ['Experimental', 'Read + write', '1,024 channels', '6-character names'],
    baudRate: 9600,
    channelMinimum: 1,
    channelMaximum: 1024,
    nameLength: 6,
    memorySize: 22217,
    layout: 'ft8800',
    checksumOffset: 0x56c8,
    powerLevels: ['Hi', 'Mid1', 'Mid2', 'Low'],
  },
  {
    protocol: 'yaesu-ftx800',
    id: 'yaesu-ft-8900',
    vendor: 'Yaesu',
    model: 'FT-8900',
    summary: 'Yaesu FT-8900 quad-band mobile.',
    tags: ['Experimental', 'Read + write', '799 channels', '6-character names'],
    baudRate: 9600,
    channelMinimum: 1,
    channelMaximum: 799,
    nameLength: 6,
    memorySize: 14793,
    layout: 'ft8900',
    checksumOffset: 0x39c8,
    powerLevels: ['Hi', 'Mid1', 'Mid2', 'Low'],
  },
]

function emptyChannel(number: number, profile: FTx800Profile): WebChannel {
  return {
    number,
    empty: true,
    name: '',
    rxFrequency: null,
    txFrequency: null,
    toneMode: 'none',
    ctcss: null,
    dtcs: null,
    dtcsPolarity: 'N',
    mode: 'FM',
    power: profile.powerLevels[0],
    skip: false,
  }
}

function decodeBcd(raw: Uint8Array): number {
  let value = 0
  for (const byte of raw) value = value * 100 + (byte >> 4) * 10 + (byte & 0x0f)
  return value
}

function decodeFrequency(raw: Uint8Array): number | null {
  if (raw.every((value) => value === 0xff)) return null
  let frequency = decodeBcd(raw) * 10_000
  if (frequency > 8_000_000_000) frequency = frequency - 8_000_000_000 + 5000
  if (frequency > 4_000_000_000) frequency = frequency - 4_000_000_000 + 2500
  return frequency
}

function encodeFrequency(frequency: number, target: Uint8Array): void {
  let value = Math.floor(frequency / 10_000)
  for (let index = 2; index >= 0; index -= 1) {
    const part = value % 100
    target[index] = ((Math.floor(part / 10) << 4) | (part % 10)) & 0xff
    value = Math.floor(value / 100)
  }
  if (frequency % 1000 === 500) target[0] |= 0x40
  if (frequency % 10_000 >= 5000) target[0] |= 0x80
}

function channelOffset(profile: FTx800Profile, number: number): number {
  if (profile.layout === 'ft8900') return 0x0708 + (number - 1) * 16
  return number <= 512
    ? 0x0948 + (number - 1) * 16
    : 0x2948 + (number - 513) * 16
}

function skipOffset(profile: FTx800Profile, number: number): number {
  return profile.layout === 'ft8900'
    ? 0x51c8 + Math.floor((number - 1) / 4)
    : 0x51c8 + Math.floor((number - 1) / 4)
}

function decodeName(raw: Uint8Array, valid: boolean): string {
  if (!valid) return ''
  return String.fromCharCode(
    ...raw.map((value) => {
      const index = value & 0x3f
      return CHARSET.charCodeAt(index)
    }),
  ).trim()
}

function decodeOffsetPacked(raw: Uint8Array, nameStart: number): number {
  let value = 0
  for (const byte of raw.slice(nameStart + 2, nameStart + 6)) {
    value = (value << 2) | ((byte & 0xc0) >> 6)
  }
  return value * 50_000
}

export function parseFTx800Channel(
  image: Uint8Array,
  profile: FTx800Profile,
  number: number,
): WebChannel {
  const raw = image.slice(channelOffset(profile, number), channelOffset(profile, number) + 16)
  if ((raw[0] & 0x01) === 0) return emptyChannel(number, profile)
  const rxFrequency = decodeFrequency(raw.slice(1, 4))
  if (!rxFrequency) return emptyChannel(number, profile)
  const duplex = (raw[0] >> 5) & 0x07
  const shift = decodeOffsetPacked(
    raw,
    10,
  )
  const txFrequency =
    duplex === 2
      ? rxFrequency - shift
      : duplex === 3
        ? rxFrequency + shift
        : duplex === 4
          ? decodeFrequency(raw.slice(
              profile.layout === 'ft8900' ? 5 : 5,
              profile.layout === 'ft8900' ? 8 : 8,
            ))
          : rxFrequency
  const modeIndex = profile.layout === 'ft8900'
    ? raw[4] & 0x03
    : raw[0] & 0x04
      ? raw[0] & 0x08
        ? 2
        : 1
      : 0
  const mode = profile.layout === 'ft8900'
    ? modeIndex === 1
      ? 'NFM'
      : modeIndex === 2
        ? 'AM'
        : 'FM'
    : (raw[0] & 0x08) !== 0
      ? 'AM'
      : (raw[0] & 0x04) !== 0
        ? 'NFM'
        : 'FM'
  const tmode = profile.layout === 'ft8900'
    ? (raw[4] >> 6) & 0x03
    : (raw[4] >> 6) & 0x03
  const power = profile.layout === 'ft8900'
    ? (raw[4] >> 4) & 0x03
    : (raw[4] >> 4) & 0x03
  const skipValue = profile.layout === 'ft8900'
    ? (raw[0] >> 1) & 0x03
    : (image[skipOffset(profile, number)] >> (((number - 1) & 3) * 2)) & 0x03
  const toneValue = profile.layout === 'ft8900'
    ? (raw[8] >> 2) & 0x3f
    : (raw[8] >> 2) & 0x3f
  const dtcsValue = profile.layout === 'ft8900'
    ? (raw[9] >> 1) & 0x7f
    : (raw[9] >> 1) & 0x7f
  const nameRaw = profile.layout === 'ft8900' ? raw.slice(10, 16) : raw.slice(10, 16)
  const nameValid = profile.layout === 'ft8900'
    ? (raw[9] & 0x01) !== 0
    : (raw[9] & 0x01) !== 0
  return {
    number,
    empty: false,
    name: decodeName(nameRaw, nameValid),
    rxFrequency,
    txFrequency,
    toneMode: tmode === 1 || tmode === 2 ? 'ctcss' : tmode === 3 ? 'dtcs' : 'none',
    ctcss: tmode === 1 || tmode === 2 ? CTCSS_TONES[toneValue] ?? null : null,
    dtcs: tmode === 3 ? DTCS_CODES[dtcsValue] ?? null : null,
    dtcsPolarity: 'N',
    mode,
    power: profile.powerLevels[power] ?? profile.powerLevels[0],
    skip: skipValue !== 0,
  }
}

export function parseFTx800Channels(
  image: Uint8Array,
  profile: FTx800Profile,
): WebChannel[] {
  return Array.from(
    { length: profile.channelMaximum },
    (_, index) => parseFTx800Channel(image, profile, index + 1),
  )
}

export function applyFTx800Channel(
  image: Uint8Array,
  profile: FTx800Profile,
  channel: WebChannel,
): void {
  const raw = image.subarray(channelOffset(profile, channel.number), channelOffset(profile, channel.number) + 16)
  if (channel.empty) {
    raw[0] &= 0xfe
    return
  }
  if (!channel.rxFrequency) throw new Error('A receive frequency is required.')
  raw[0] |= 0x01
  encodeFrequency(channel.rxFrequency, raw.subarray(1, 4))
  const delta = channel.txFrequency ? channel.txFrequency - channel.rxFrequency : 0
  raw[0] = (raw[0] & 0x1f) | (((delta < 0 ? 2 : delta > 0 ? 3 : 1) & 0x07) << 5)
  if (profile.layout === 'ft8800') {
    raw[0] = (raw[0] & ~0x0c) |
      (channel.mode === 'NFM' ? 0x04 : 0) |
      (channel.mode === 'AM' ? 0x08 : 0)
    raw[4] =
      (raw[4] & 0x0f) |
      ((Math.max(0, profile.powerLevels.indexOf(channel.power)) & 0x03) << 4) |
      ((channel.toneMode === 'dtcs' ? 3 : channel.toneMode === 'ctcss' ? 1 : 0) << 6)
    raw[9] = (raw[9] & 0x7e) |
      ((DTCS_CODES.indexOf(channel.dtcs as (typeof DTCS_CODES)[number]) & 0x7f) << 1) |
      (channel.name ? 1 : 0)
    raw.set(encodeNameBlock(channel.name), 10)
    encodePackedOffset(raw, 10, Math.abs(delta))
  } else {
    raw[4] =
      (channel.mode === 'NFM' ? 1 : channel.mode === 'AM' ? 2 : 0) |
      (channel.name ? 0x04 : 0) |
      ((Math.max(0, profile.powerLevels.indexOf(channel.power)) & 0x03) << 4) |
      ((channel.toneMode === 'dtcs' ? 3 : channel.toneMode === 'ctcss' ? 1 : 0) << 6)
    raw[9] =
      (channel.name ? 1 : 0) |
      ((DTCS_CODES.indexOf(channel.dtcs as (typeof DTCS_CODES)[number]) & 0x7f) << 1)
    raw.set(encodeNameBlock(channel.name), 10)
    encodePackedOffset(raw, 10, Math.abs(delta))
    raw[0] =
      (raw[0] & ~0x06) |
      ((channel.skip ? 1 : 0) << 1)
  }
  if (profile.layout !== 'ft8900') {
    const offset = skipOffset(profile, channel.number)
    const shift = ((channel.number - 1) & 3) * 2
    image[offset] = (image[offset] & ~(0x03 << shift)) | ((channel.skip ? 1 : 0) << shift)
  }
}

function encodePackedOffset(raw: Uint8Array, nameStart: number, delta: number): void {
  let value = Math.round(delta / 50_000)
  for (let index = 5; index >= 2; index -= 1) {
    raw[nameStart + index] =
      (raw[nameStart + index] & 0x3f) | ((value & 0x03) << 6)
    value >>= 2
  }
}

function encodeNameBlock(name: string): Uint8Array {
  const result = new Uint8Array(6)
  const normalized = name.toUpperCase().slice(0, 6).padEnd(6, ' ')
  for (let index = 0; index < 6; index += 1) {
    const code = CHARSET.indexOf(normalized[index])
    result[index] = code < 0 ? CHARSET.indexOf(' ') : code
  }
  return result
}

function updateChecksum(image: Uint8Array, offset: number): void {
  let total = 0
  for (let index = 0; index < offset; index += 1) total = (total + image[index]) & 0xff
  image[offset] = total
}

export async function readFTx800RadioWithTransport(
  profile: FTx800Profile,
  transport: RadioTransport,
  onProgress?: (progress: RadioProgress) => void,
): Promise<{ image: Uint8Array; reportedModel: string }> {
  await transport.discardInput()
  const image = new Uint8Array(profile.memorySize)
  image.set(await transport.readExact(8, 12_000), 0)
  await transport.write(Uint8Array.of(ACK))
  const total = Math.ceil((profile.memorySize - 9) / BLOCK_SIZE)
  for (let index = 0; index < total; index += 1) {
    const offset = 8 + index * BLOCK_SIZE
    const length = Math.min(BLOCK_SIZE, profile.memorySize - 1 - offset)
    if (length > 0) image.set(await transport.readExact(length, 5000), offset)
    await transport.write(Uint8Array.of(ACK))
    onProgress?.({ current: index + 1, total, label: 'Reading Yaesu FT-x800 memory' })
  }
  image[profile.memorySize - 1] = (await transport.readExact(1, 3000))[0]
  await transport.write(Uint8Array.of(ACK))
  return { image, reportedModel: profile.model }
}

export async function writeFTx800RadioWithTransport(
  profile: FTx800Profile,
  image: Uint8Array,
  transport: RadioTransport,
  onProgress?: (progress: RadioProgress) => void,
): Promise<void> {
  if (image.byteLength !== profile.memorySize) throw new Error('Image size does not match this Yaesu model.')
  updateChecksum(image, profile.checksumOffset)
  const total = Math.ceil(profile.memorySize / BLOCK_SIZE)
  for (let index = 0; index < total; index += 1) {
    const offset = index * BLOCK_SIZE
    const block = image.slice(offset, Math.min(image.byteLength, offset + BLOCK_SIZE))
    if (!block.length) break
    await transport.write(block)
    if ((await transport.readExact(1, 3000))[0] !== ACK) throw new Error(`The radio rejected block ${index}.`)
    onProgress?.({ current: index + 1, total, label: 'Writing Yaesu FT-x800 memory' })
  }
}
